import { useState, useEffect } from 'react';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Eye, RotateCcw, FileText, Receipt, FileCheck, ClipboardList, Check } from 'lucide-react';
import { confirmAsync } from '@/lib/confirm';

const DOCUMENT_TYPES = [
  { value: 'FACTURA', label: 'Factura', icon: FileText, color: 'bg-blue-100 text-blue-800' },
  { value: 'ABONO', label: 'Abono/Rectificativa', icon: Receipt, color: 'bg-red-100 text-red-800' },
  { value: 'PRESUPUESTO', label: 'Presupuesto', icon: ClipboardList, color: 'bg-green-100 text-green-800' },
  { value: 'PROFORMA', label: 'Proforma', icon: FileCheck, color: 'bg-purple-100 text-purple-800' },
];

const RESET_POLICIES = [
  { value: 'ANUAL', label: 'Anual (reinicia cada año)' },
  { value: 'MENSUAL', label: 'Mensual (reinicia cada mes)' },
  { value: 'NUNCA', label: 'Nunca (correlativo continuo)' },
];

const MASK_VARIABLES = [
  { var: '{PREFIX}', desc: 'Código de la serie (ej: F, AB)' },
  { var: '{SEQ:4}', desc: 'Número secuencial con 4 dígitos (0001)' },
  { var: '{SEQ:6}', desc: 'Número secuencial con 6 dígitos (000001)' },
  { var: '{YEAR}', desc: 'Año completo (2025)' },
  { var: '{YY}', desc: 'Año corto (25)' },
  { var: '{MONTH}', desc: 'Mes (01-12)' },
];

export default function DocumentSeriesConfig() {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingSeries, setEditingSeries] = useState(null);
  const [previewNumber, setPreviewNumber] = useState('');
  
  const [formData, setFormData] = useState({
    nombre: '',
    codigo: '',
    tipo_documento: 'FACTURA',
    mask: '{PREFIX}-{SEQ:4}-{YEAR}',
    next_number: 1,
    reset_policy: 'ANUAL',
    is_default: false,
    is_active: true,
  });

  useEffect(() => {
    loadSeries();
  }, []);

  useEffect(() => {
    // Actualizar preview cuando cambia la máscara o código
    updatePreview();
  }, [formData.mask, formData.codigo, formData.next_number]);

  const loadSeries = async () => {
    try {
      const response = await axiosInstance.get('/document-series');
      setSeries(response.data);
    } catch (error) {
      console.error('Error loading series:', error);
      // Si no hay series, mostrar opción de inicializar
      if (error.response?.status === 404 || series.length === 0) {
        toast.info('No hay series configuradas. Puedes crear las series por defecto.');
      }
    } finally {
      setLoading(false);
    }
  };

  const updatePreview = () => {
    if (!formData.mask || !formData.codigo) {
      setPreviewNumber('');
      return;
    }

    let preview = formData.mask;
    const now = new Date();
    
    preview = preview.replace('{PREFIX}', formData.codigo);
    preview = preview.replace('{YEAR}', now.getFullYear().toString());
    preview = preview.replace('{YY}', now.getFullYear().toString().slice(-2));
    preview = preview.replace('{MONTH}', (now.getMonth() + 1).toString().padStart(2, '0'));
    
    // Reemplazar {SEQ:n}
    const seqMatch = preview.match(/\{SEQ:(\d+)\}/);
    if (seqMatch) {
      const padding = parseInt(seqMatch[1]);
      const seqStr = (formData.next_number || 1).toString().padStart(padding, '0');
      preview = preview.replace(/\{SEQ:\d+\}/, seqStr);
    }
    
    setPreviewNumber(preview);
  };

  const handleInitDefaults = async () => {
    try {
      const response = await axiosInstance.post('/document-series/init-defaults');
      if (response.data.success) {
        toast.success(response.data.message);
        loadSeries();
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error('Error al inicializar series');
    }
  };

  const openCreateDialog = () => {
    setEditingSeries(null);
    setFormData({
      nombre: '',
      codigo: '',
      tipo_documento: 'FACTURA',
      mask: '{PREFIX}-{SEQ:4}-{YEAR}',
      next_number: 1,
      reset_policy: 'ANUAL',
      is_default: false,
      is_active: true,
    });
    setShowDialog(true);
  };

  const openEditDialog = (s) => {
    setEditingSeries(s);
    setFormData({
      nombre: s.nombre,
      codigo: s.codigo,
      tipo_documento: s.tipo_documento,
      mask: s.mask,
      next_number: s.next_number,
      reset_policy: s.reset_policy,
      is_default: s.is_default,
      is_active: s.is_active,
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    // Validaciones
    if (!formData.nombre || !formData.codigo || !formData.mask) {
      toast.error('Completa todos los campos obligatorios');
      return;
    }

    if (!formData.mask.includes('{SEQ:')) {
      toast.error('La máscara debe contener {SEQ:n} para el número secuencial');
      return;
    }

    try {
      if (editingSeries) {
        await axiosInstance.put(`/document-series/${editingSeries.id}`, formData);
        toast.success('Serie actualizada correctamente');
      } else {
        await axiosInstance.post('/document-series', formData);
        toast.success('Serie creada correctamente');
      }
      setShowDialog(false);
      loadSeries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar la serie');
    }
  };

  const handleDelete = async (s) => {
    if (!(await confirmAsync(`¿Eliminar la serie "${s.nombre}"?`))) return;

    try {
      await axiosInstance.delete(`/document-series/${s.id}`);
      toast.success('Serie eliminada');
      loadSeries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleResetCounter = async (s) => {
    const newValue = window.prompt('Nuevo valor del contador:', '1');
    if (newValue === null) return;

    const num = parseInt(newValue);
    if (isNaN(num) || num < 1) {
      toast.error('Introduce un número válido mayor que 0');
      return;
    }

    try {
      await axiosInstance.post(`/document-series/${s.id}/reset-counter?new_value=${num}`);
      toast.success(`Contador reiniciado a ${num}`);
      loadSeries();
    } catch (error) {
      toast.error('Error al reiniciar contador');
    }
  };

  const handleSetDefault = async (s) => {
    try {
      await axiosInstance.put(`/document-series/${s.id}`, { is_default: true });
      toast.success(`"${s.nombre}" es ahora la serie por defecto`);
      loadSeries();
    } catch (error) {
      toast.error('Error al establecer como defecto');
    }
  };

  const getDocTypeInfo = (tipo) => {
    return DOCUMENT_TYPES.find(t => t.value === tipo) || DOCUMENT_TYPES[0];
  };

  // Agrupar series por tipo de documento
  const groupedSeries = DOCUMENT_TYPES.map(type => ({
    ...type,
    series: series.filter(s => s.tipo_documento === type.value)
  }));

  if (loading) {
    return <div className="text-center py-8 text-gray-500">Cargando series...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Series de Numeración</h3>
          <p className="text-sm text-gray-500">
            Configura las series de numeración para facturas, abonos, presupuestos y proformas
          </p>
        </div>
        <div className="flex gap-2">
          {series.length === 0 && (
            <Button variant="outline" onClick={handleInitDefaults}>
              <RotateCcw className="w-4 h-4 mr-2" />
              Crear Series por Defecto
            </Button>
          )}
          <Button onClick={openCreateDialog}>
            <Plus className="w-4 h-4 mr-2" />
            Nueva Serie
          </Button>
        </div>
      </div>

      {/* Series por tipo de documento */}
      {series.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 mb-4">No hay series configuradas</p>
            <Button onClick={handleInitDefaults}>
              Crear Series por Defecto
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {groupedSeries.map(group => (
            <Card key={group.value}>
              <CardHeader className="py-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <group.icon className="w-5 h-5" />
                  {group.label}
                  <Badge variant="outline" className="ml-2">
                    {group.series.length} serie{group.series.length !== 1 ? 's' : ''}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {group.series.length === 0 ? (
                  <p className="text-sm text-gray-500 py-2">
                    No hay series para este tipo. 
                    <Button variant="link" className="px-1" onClick={openCreateDialog}>
                      Crear una
                    </Button>
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nombre</TableHead>
                        <TableHead>Código</TableHead>
                        <TableHead>Máscara</TableHead>
                        <TableHead>Próximo Nº</TableHead>
                        <TableHead>Reset</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {group.series.map(s => (
                        <TableRow key={s.id}>
                          <TableCell className="font-medium">
                            {s.nombre}
                            {s.is_default && (
                              <Badge className="ml-2 bg-green-100 text-green-800">
                                <Check className="w-3 h-3 mr-1" />
                                Defecto
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <code className="bg-gray-100 px-2 py-1 rounded text-sm">{s.codigo}</code>
                          </TableCell>
                          <TableCell>
                            <code className="bg-gray-100 px-2 py-1 rounded text-xs">{s.mask}</code>
                          </TableCell>
                          <TableCell>
                            <span className="font-mono">{s.preview_next}</span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-gray-600">
                              {RESET_POLICIES.find(p => p.value === s.reset_policy)?.label.split(' ')[0]}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge className={s.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                              {s.is_active ? 'Activa' : 'Inactiva'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end">
                              {!s.is_default && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleSetDefault(s)}
                                  title="Establecer como defecto"
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleResetCounter(s)}
                                title="Reiniciar contador"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(s)}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(s)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog para crear/editar */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingSeries ? 'Editar Serie' : 'Nueva Serie de Numeración'}
            </DialogTitle>
            <DialogDescription>
              {editingSeries 
                ? 'Modifica los datos de la serie de numeración'
                : 'Crea una nueva serie de numeración para tus documentos'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Nombre */}
            <div className="space-y-2">
              <Label>Nombre de la serie *</Label>
              <Input
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                placeholder="Ej: Facturas estándar"
              />
            </div>

            {/* Tipo de documento */}
            <div className="space-y-2">
              <Label>Tipo de documento *</Label>
              <Select
                value={formData.tipo_documento}
                onValueChange={(v) => setFormData({ ...formData, tipo_documento: v })}
                disabled={!!editingSeries}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Código y Máscara */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Código/Prefijo *</Label>
                <Input
                  value={formData.codigo}
                  onChange={(e) => setFormData({ ...formData, codigo: e.target.value.toUpperCase() })}
                  placeholder="F"
                  maxLength={10}
                />
              </div>
              <div className="space-y-2">
                <Label>Próximo número</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.next_number}
                  onChange={(e) => setFormData({ ...formData, next_number: parseInt(e.target.value) || 1 })}
                />
              </div>
            </div>

            {/* Máscara */}
            <div className="space-y-2">
              <Label>Máscara de numeración *</Label>
              <Input
                value={formData.mask}
                onChange={(e) => setFormData({ ...formData, mask: e.target.value })}
                placeholder="{PREFIX}-{SEQ:4}-{YEAR}"
              />
              <div className="text-xs text-gray-500 space-y-1">
                <p><strong>Variables disponibles:</strong></p>
                <div className="grid grid-cols-2 gap-1">
                  {MASK_VARIABLES.map(v => (
                    <span key={v.var}>
                      <code className="bg-gray-100 px-1">{v.var}</code> {v.desc}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Preview */}
            {previewNumber && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-700">
                  <Eye className="w-4 h-4 inline mr-1" />
                  <strong>Vista previa:</strong>{' '}
                  <span className="font-mono text-lg">{previewNumber}</span>
                </p>
              </div>
            )}

            {/* Política de reset */}
            <div className="space-y-2">
              <Label>Política de reinicio</Label>
              <Select
                value={formData.reset_policy}
                onValueChange={(v) => setFormData({ ...formData, reset_policy: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RESET_POLICIES.map(p => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Switches */}
            <div className="flex gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_default}
                  onCheckedChange={(v) => setFormData({ ...formData, is_default: v })}
                />
                <Label>Serie por defecto</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                />
                <Label>Activa</Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave}>
              {editingSeries ? 'Guardar Cambios' : 'Crear Serie'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
